import { sanitizeText } from "@certa/common/utils/sanitizeHTML";
import type { User } from "@certa/queries/types/studio.types";
import { FieldType } from "@certa/types";
import slugifyLib from "slugify";
import format from "date-fns/format";

export const getUserDisplayName = (user: User, withEmail: boolean = true) => {
  const { email, firstName, lastName } = user;
  const fullName = [firstName, lastName].join(" ").trim();
  return fullName ? (withEmail ? `${fullName} (${email})` : fullName) : email;
};

type JSONSanitzerFlags = {
  removeNull?: boolean;
  removeEmptyObject?: boolean;
  removeEmptyString?: boolean;
};

const defaultSanitizationFlags: JSONSanitzerFlags = {
  removeNull: true
};

export const sanitizeJSONTransform = (
  value: any,
  flags: JSONSanitzerFlags = defaultSanitizationFlags
) =>
  (flags.removeNull && value === null) ||
  (flags.removeEmptyString && value === "") ||
  (flags.removeEmptyObject &&
    value && // has a truthy value
    typeof value === "object" && // is an object
    !Array.isArray(value) && // but not an array
    Object.keys(value).length === 0) // and doesn't have any keys
    ? undefined
    : value;

export const sanitizeJSONAndExportAsFile = (
  jsonData: Object,
  fileNamePrefix: string,
  isECAEnabled?: boolean
) => {
  const dataStr = JSON.stringify(jsonData);
  const sanitizedJSONData = JSON.parse(dataStr, (key, value) =>
    sanitizeJSONTransform(value, {
      removeNull: !isECAEnabled
    })
  );

  const dataStrSanitized = JSON.stringify(sanitizedJSONData);
  const dataUri =
    "data:application/json;charset=utf-8," +
    encodeURIComponent(dataStrSanitized);

  const exportFileDefaultName = `${fileNamePrefix}_${format(
    new Date(),
    "dd-MMM-yyyy_HH-mm-ss" // 01-Jan-2024_13-30-30
  )}.json`;

  const linkElement = document.createElement("a");
  linkElement.setAttribute("href", dataUri);
  linkElement.setAttribute("download", exportFileDefaultName);
  linkElement.click();
};

const isHTMLSupportedFieldType = (fieldType: FieldType) =>
  [FieldType.INFO_DESCRIPTION_BOX, FieldType.PARAGRAPH].includes(fieldType);

export const sanitizeHTMLText = (value: string, fieldtype?: FieldType) => {
  if (!!fieldtype) {
    if (isHTMLSupportedFieldType(fieldtype)) {
      return sanitizeText(value);
    }

    return value;
  }
  // by default sanitize if field type is not provided.
  return sanitizeText(value);
};

export const slugify = (val: string = "", seperator = "_") =>
  slugifyLib(val.trim().replace(/[\W_]+/g, " "), seperator).toLowerCase();

export const hexCodeRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;

export const checkIsValidRegex = (regexString: string) => {
  try {
    new RegExp(regexString);
    return true;
  } catch (e) {
    return false;
  }
};

/**
 * Generic utility function to remove all __studio_* fields from an object
 */
export const removeStudioParams = <T extends Record<string, unknown>>(
  obj: T
): Omit<T, `__studio_${string}`> => {
  const result = {} as Omit<T, `__studio_${string}`>;

  for (const key in obj) {
    if (!key.startsWith("__studio_")) {
      (result as Record<string, unknown>)[key] = obj[key];
    }
  }

  return result;
};
